import Cookies from "js-cookie";
import useAnonymousId from "lib/hooks/useAnonymousId";
import { ThirdPartyAuthType } from "lib/types/checkout";
import {
  clearAuthLocalStorage,
  getAccessToken,
  setAuthCookies,
  setAuthCookiesInLocalStorage,
} from "lib/utils/auth";
import { constants, eventTypes } from "lib/utils/constants";
import { inIframe, isThirdPartyCookieEnabled, publishPostMessage } from "lib/utils/helpers";
import { createContext, useCallback, useContext, useMemo, useReducer, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useMerchantContext } from "lib/contexts/MerchantProvider";

const Context = createContext<AuthContext>({ state: {}, actions: {} } as AuthContext);

export enum ActionType {
  SET_LOGGED_IN,
  SET_LOCK_DISCOUNTS,
  SET_THIRDPARTY_AUTH,
  SET_LOGGED_OUT,
  LOGIN,
  LOGOUT,
  SET_IS_AUTHENTICATING,
  SET_USER_TYPE,
  SET_STORE_LOGIN_STATE,
  ß,
}

export type Action =
  | { type: ActionType.SET_LOGGED_IN; payload: boolean; loginType?: "COOKIE" | "AUTH" }
  | { type: ActionType.SET_THIRDPARTY_AUTH; payload: ThirdPartyAuthType[] | [] }
  | { type: ActionType.SET_LOCK_DISCOUNTS; payload: boolean }
  | { type: ActionType.SET_LOGGED_OUT; payload: boolean }
  | { type: ActionType.SET_IS_AUTHENTICATING; payload: boolean }
  | { type: ActionType.SET_STORE_LOGIN_STATE; payload: boolean }
  | { type: ActionType.SET_USER_TYPE; payload?: "OLD" | "NEW" };

export interface AuthState {
  isAuthenticated: boolean;
  lockDiscounts: boolean;
  thirdPartyAuth: ThirdPartyAuthType[] | [];
  isLoggedOut: boolean;
  isAuthenticating: boolean;
  loginType?: "COOKIE" | "AUTH";
  userType?: "OLD" | "NEW";
  /**
   * Whether the shopper is logged in on the merchant's store.
   *
   * Distinct from isAuthenticated, which is this iframe's own session. The two
   * disagree in both directions: partitioned cookies leave the iframe logged
   * out while the store is logged in, and a store logout can leave a live
   * Shopflo cookie behind here.
   *
   * Undefined until the storefront script answers - that is a real third state,
   * not a false, and callers should wait rather than assume either way.
   */
  isLoggedInOnStore?: boolean;
}

export interface AuthContext {
  state: AuthState;
  actions: {
    setLoggedIn: (value: boolean, payload?: { loginType?: "COOKIE" | "AUTH" }) => void;
    setLockDiscounts: (value: boolean) => void;
    setThirdPartyAuth: (data: ThirdPartyAuthType[] | []) => void;
    setLoggedOut: (value: boolean) => void;
    login: (
      idToken: string,
      refreshToken: string,
      authExpiry?: number,
      refreshExpiry?: number,
      shouldUpdateCart?: boolean,
    ) => void;
    /** fromStore: the store logged out first, so nothing is sent back to it */
    logout: (isCart?: boolean, options?: { fromStore?: boolean }) => void;
    setIsAuthenticating: (value: boolean) => void;
    setUserType: (value: "OLD" | "NEW") => void;
  };
}

function reducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case ActionType.SET_LOGGED_IN: {
      return {
        ...state,
        isAuthenticated: action.payload,
        loginType: action.loginType,
      };
    }
    case ActionType.SET_LOCK_DISCOUNTS: {
      return {
        ...state,
        lockDiscounts: action.payload,
      };
    }
    case ActionType.SET_THIRDPARTY_AUTH: {
      return {
        ...state,
        thirdPartyAuth: action.payload,
      };
    }
    case ActionType.SET_LOGGED_OUT: {
      return {
        ...state,
        isLoggedOut: action.payload,
      };
    }
    case ActionType.SET_IS_AUTHENTICATING: {
      return {
        ...state,
        isAuthenticating: action.payload,
      };
    }
    case ActionType.SET_USER_TYPE: {
      return {
        ...state,
        userType: action.payload,
      };
    }
    case ActionType.SET_STORE_LOGIN_STATE: {
      return {
        ...state,
        isLoggedInOnStore: action.payload,
      };
    }
  }
  return state;
}

const AuthInitialState: AuthState = {
  isAuthenticated: Boolean(getAccessToken()),
  lockDiscounts: false,
  thirdPartyAuth: [],
  isLoggedOut: false,
  isAuthenticating: true,
  userType: "OLD"
};

export const AuthProvider: React.FC<React.PropsWithChildren<{ initialState?: AuthState }>> = ({
  initialState = AuthInitialState,
  children,
}) => {
  const [reducerState, dispatch] = useReducer(reducer, initialState);
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetUserMapping } = useAnonymousId();
  const {
    state: {
      consentState: { isConsentEnabled },
    },
  } = useMerchantContext();
  const setLoggedIn = useCallback(
    (loggedIn: boolean, { loginType }: { loginType?: "COOKIE" | "AUTH" } = {}) => {
      dispatch({
        type: ActionType.SET_LOGGED_IN,
        payload: loggedIn,
        loginType,
      });
    },
    [],
  );

  const setLockDiscounts = useCallback((lockDiscounts: boolean) => {
    dispatch({
      type: ActionType.SET_LOCK_DISCOUNTS,
      payload: lockDiscounts,
    });
  }, []);

  const setThirdPartyAuth = useCallback((data: ThirdPartyAuthType[] | []) => {
    dispatch({
      type: ActionType.SET_THIRDPARTY_AUTH,
      payload: data,
    });
  }, []);

  const setLoggedOut = useCallback((loggedOut: boolean) => {
    dispatch({
      type: ActionType.SET_LOGGED_OUT,
      payload: loggedOut,
    });
  }, []);

  const login = useCallback(
    (
      idToken: string,
      refreshToken: string,
      authExpiry?: number,
      refreshExpiry?: number,
      shouldUpdateCart?: boolean,
    ) => {
      try {
        setAuthCookies(idToken, refreshToken, authExpiry, refreshExpiry);
        // Cookies are silently dropped when third-party cookies are blocked; localStorage is the fallback
        setAuthCookiesInLocalStorage(idToken, refreshToken, authExpiry, refreshExpiry);
        setLockDiscounts(true);
        setLoggedIn(true);
        if (Boolean(shouldUpdateCart) && isThirdPartyCookieEnabled()) {
          publishPostMessage(eventTypes.LOGIN_FROM_CHECKOUT, {
            idToken,
            refreshToken,
            authExpiry,
            refreshExpiry,
          });
        }
      } catch (err) {
        setLoggedIn(false);
        console.error(err);
      }
    },
    [setLockDiscounts, setLoggedIn],
  );

  const logout = useCallback(
    (isCart?: boolean, { fromStore = false }: { fromStore?: boolean } = {}) => {
      Cookies.remove(constants.AUTH_COOKIE_CLIENT, { path: "/", sameSite: "none", secure: true });
      Cookies.remove(constants.REFRESH_TOKEN_COOKIE_CLIENT, { path: "/", sameSite: "none", secure: true });
      localStorage.removeItem(constants.ORDER_HISTORY_COOKIE_CLIENT);
      clearAuthLocalStorage();
      resetUserMapping();
      setLoggedOut(true);
      setLockDiscounts(true);
      setLoggedIn(false);
      // The store started this logout and is navigating away: don't message it back.
      if (fromStore) return;
      // DPDPA: end the store's session too, so it can't sign this iframe back in
      // or keep consents on the previous shopper. The cart's logout is relayed
      // from checkout, which has already sent this.
      if (isConsentEnabled && !isCart) {
        publishPostMessage(eventTypes.FLO_STORE_LOGOUT, {});
      }
      if (!isCart && searchParams.has("checkoutId") && !searchParams.has("fromCart")) {
        publishPostMessage(eventTypes.LOGOUT_FROM_CHECKOUT, {});
        searchParams?.delete("referrer");
        searchParams?.delete("checkoutId");
        searchParams?.append("fromCart", "true");
        setSearchParams(searchParams.toString());
      }
    },
    [setLockDiscounts, setLoggedIn, searchParams, setSearchParams, isConsentEnabled],
  );

  // DPDPA: the store logged out with consent enforced, so this checkout session
  // ends too. Iframes share these cookies, so whichever confirms first has
  // cleared it for all of them; the store waits for that before navigating.
  useEffect(() => {
    const handleStoreLogout = (event: MessageEvent) => {
      if (event?.data?.type !== eventTypes.FLO_LOGOUT_FROM_STORE || !isConsentEnabled) return;
      logout(false, { fromStore: true });
      publishPostMessage(eventTypes.FLO_LOGGED_OUT_FROM_STORE, {});
    };
    window.addEventListener("message", handleStoreLogout);
    return () => window.removeEventListener("message", handleStoreLogout);
  }, [logout, isConsentEnabled]);

  const setIsAuthenticating = useCallback((isAuthenticating: boolean) => {
    dispatch({
      type: ActionType.SET_IS_AUTHENTICATING,
      payload: isAuthenticating,
    });
  }, []);

  const setUserType = useCallback((userType: "OLD" | "NEW") => {
    dispatch({
      type: ActionType.SET_USER_TYPE,
      payload: userType,
    });
  }, []);

  const actions = useMemo(
    () => ({
      setLoggedIn,
      setLockDiscounts,
      setThirdPartyAuth,
      setLoggedOut,
      login,
      logout,
      setIsAuthenticating,
      setUserType,
    }),
    [login, logout, setLockDiscounts, setLoggedIn, setThirdPartyAuth, setIsAuthenticating, setUserType],
  );

  // Track the store's own session. The script pushes this whenever it changes;
  // the request on mount covers the case where it changed - or was first
  // established - before this iframe existed to hear about it.
  useEffect(() => {
    if (!inIframe()) return;

    const handleStoreLoginState = (event: MessageEvent) => {
      if (event?.data?.type !== eventTypes.FLO_STORE_LOGIN_STATE) return;
      dispatch({
        type: ActionType.SET_STORE_LOGIN_STATE,
        payload: Boolean(event?.data?.payload?.isLoggedInOnStore),
      });
    };

    window.addEventListener("message", handleStoreLoginState);
    publishPostMessage(eventTypes.FLO_GET_STORE_LOGIN_STATE, {});

    return () => {
      window.removeEventListener("message", handleStoreLoginState);
    };
  }, []);

  // Listen for token refresh failure events and trigger logout
  useEffect(() => {
    const handleTokenRefreshFailed = () => {
      logout();
    };

    window.addEventListener("auth:token-refresh-failed", handleTokenRefreshFailed);

    return () => {
      window.removeEventListener("auth:token-refresh-failed", handleTokenRefreshFailed);
    };
  }, [logout]);

  return (
    <Context.Provider
      value={{
        state: reducerState,
        actions,
      }}>
      {children}
    </Context.Provider>
  );
};

export function useAuthContext() {
  if (!Boolean(Context)) throw new Error("useAuthContext must be used within a AuthProvider");
  const AuthContext = useContext(Context);
  return AuthContext as AuthContext;
}
